import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Loader2, Plus, Save, Trash2, Upload, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";

/**
 * Admin Studio — Assessment editor (Phase 1d).
 * Route: /admin/academy/assessments/:id
 * Tabs: Settings · Questions · Preview
 */
export default function AdminAssessmentEditor() {
  const { id } = useParams();
  const [tab, setTab] = useState("settings");
  const [state, setState] = useState({ status: "loading", assessment: null, questions: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const res = await api.get(`/admin/academy/assessments/${id}`);
      setState({ status: "ready", assessment: res.data.assessment, questions: res.data.questions || [] });
    } catch (err) {
      toast.error(formatApiError(err));
      setState((s) => ({ ...s, status: "error" }));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/academy/assessments/${id}`, updates);
      setState((s) => ({ ...s, assessment: res.data }));
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    try {
      const res = await api.post(`/admin/academy/assessments/${id}/publish`);
      setState((s) => ({ ...s, assessment: res.data }));
      toast.success("Published");
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const msg = err?.response?.data?.detail?.message || code || formatApiError(err);
      toast.error(`Publish blocked: ${msg}`);
    }
  };

  const unpublish = async () => {
    try {
      const res = await api.post(`/admin/academy/assessments/${id}/unpublish`);
      setState((s) => ({ ...s, assessment: res.data }));
      toast.success("Moved to draft");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addQuestion = async () => {
    try {
      const res = await api.post(`/admin/academy/assessments/${id}/questions`, {
        prompt_markdown: "New question — edit me.",
        kind: "single_choice",
        options: [{ id: "opt_A", text: "A" }, { id: "opt_B", text: "B" }],
        correct_option_ids: ["opt_A"],
        points: 1,
        sort_order: state.questions.length,
      });
      setState((s) => ({ ...s, questions: [...s.questions, res.data] }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const patchQuestion = async (qid, updates) => {
    try {
      const res = await api.patch(`/admin/academy/questions/${qid}`, updates);
      setState((s) => ({
        ...s,
        questions: s.questions.map((q) => (q.id === qid ? res.data : q)),
      }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteQuestion = async (qid) => {
    try {
      await api.delete(`/admin/academy/questions/${qid}`);
      setState((s) => ({ ...s, questions: s.questions.filter((q) => q.id !== qid) }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (state.status === "loading") {
    return (
      <div data-testid="admin-assessment-editor-loading" className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  if (state.status !== "ready" || !state.assessment) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 text-v2-text">
        Could not load assessment.
      </div>
    );
  }

  const a = state.assessment;
  const isPublished = a.status === "published";

  return (
    <div data-testid="admin-assessment-editor" className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <div className="mb-6 flex items-center gap-3 text-sm text-v2-text-muted">
        <Link
          to="/admin/academy/assessments"
          data-testid="admin-assessment-editor-back"
          className="inline-flex items-center gap-1.5 hover:text-v2-text"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Assessments
        </Link>
      </div>
      <header className="mb-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">
            {a.kind} · {a.scope} · {a.status}
          </p>
          <h1
            data-testid="admin-assessment-editor-title"
            className="mt-1 text-[28px] font-semibold text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            {a.title}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isPublished ? (
            <button
              type="button"
              onClick={unpublish}
              data-testid="admin-assessment-unpublish-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-v2-border text-v2-text hover:bg-v2-surface-muted"
            >
              Move to draft
            </button>
          ) : (
            <button
              type="button"
              onClick={publish}
              data-testid="admin-assessment-publish-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--v2-accent)] text-white font-semibold hover:opacity-90"
            >
              <Upload className="w-4 h-4" />
              Publish
            </button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="mb-6 flex items-center gap-2 border-b border-v2-border">
        {[
          { id: "settings", label: "Settings" },
          { id: "questions", label: `Questions (${state.questions.length})` },
          { id: "preview", label: "Preview" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`admin-assessment-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${
              tab === t.id
                ? "border-[var(--v2-accent)] text-v2-text"
                : "border-transparent text-v2-text-muted hover:text-v2-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "settings" ? (
        <SettingsTab assessment={a} onPatch={patch} saving={saving} />
      ) : null}
      {tab === "questions" ? (
        <QuestionsTab
          questions={state.questions}
          onAdd={addQuestion}
          onPatch={patchQuestion}
          onDelete={deleteQuestion}
        />
      ) : null}
      {tab === "preview" ? <PreviewTab assessmentId={id} /> : null}
    </div>
  );
}

// -----------------------------
// Settings tab
// -----------------------------

function SettingsTab({ assessment, onPatch, saving }) {
  const [form, setForm] = useState({
    title: assessment.title || "",
    description: assessment.description || "",
    pass_pct: assessment.pass_pct ?? 80,
    attempts_allowed: assessment.attempts_allowed ?? 3,
    time_limit_seconds: assessment.time_limit_seconds ?? "",
    feedback_policy: assessment.feedback_policy || "score_only",
    required_for_completion: Boolean(assessment.required_for_completion),
    randomize_questions: Boolean(assessment.randomize_questions),
    randomize_options: Boolean(assessment.randomize_options),
  });

  const save = () => {
    const payload = { ...form };
    if (payload.time_limit_seconds === "" || payload.time_limit_seconds == null) {
      delete payload.time_limit_seconds;
    } else {
      payload.time_limit_seconds = Number(payload.time_limit_seconds);
    }
    payload.pass_pct = Number(payload.pass_pct);
    payload.attempts_allowed = Number(payload.attempts_allowed);
    onPatch(payload);
  };

  const inputCls = "w-full rounded-lg border border-v2-border bg-v2-background px-3 py-2 text-v2-text";

  return (
    <div data-testid="admin-assessment-settings" className="rounded-xl border border-v2-border bg-v2-surface p-6 space-y-4">
      <Field label="Title">
        <input data-testid="admin-assessment-settings-title" className={inputCls}
          value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
      </Field>
      <Field label="Description">
        <textarea data-testid="admin-assessment-settings-description" className={`${inputCls} h-24 resize-none`}
          value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Passing %">
          <input type="number" min="0" max="100" data-testid="admin-assessment-settings-pass-pct" className={inputCls}
            value={form.pass_pct} onChange={(e) => setForm((f) => ({ ...f, pass_pct: e.target.value }))} />
        </Field>
        <Field label="Max attempts (0 = unlimited)">
          <input type="number" min="0" data-testid="admin-assessment-settings-attempts" className={inputCls}
            value={form.attempts_allowed} onChange={(e) => setForm((f) => ({ ...f, attempts_allowed: e.target.value }))} />
        </Field>
        <Field label="Time limit (seconds, empty = none)">
          <input type="number" min="1" data-testid="admin-assessment-settings-time-limit" className={inputCls}
            value={form.time_limit_seconds} onChange={(e) => setForm((f) => ({ ...f, time_limit_seconds: e.target.value }))} />
        </Field>
      </div>
      <Field label="Feedback policy">
        <select data-testid="admin-assessment-settings-feedback" className={inputCls}
          value={form.feedback_policy} onChange={(e) => setForm((f) => ({ ...f, feedback_policy: e.target.value }))}>
          <option value="none">None</option>
          <option value="score_only">Score only</option>
          <option value="per_q">Per question (right/wrong)</option>
          <option value="full">Full (answers + explanations)</option>
        </select>
      </Field>
      <div className="flex flex-wrap gap-4">
        <ToggleField
          label="Required for completion"
          checked={form.required_for_completion}
          onChange={(v) => setForm((f) => ({ ...f, required_for_completion: v }))}
          testId="admin-assessment-settings-required"
        />
        <ToggleField
          label="Randomize question order"
          checked={form.randomize_questions}
          onChange={(v) => setForm((f) => ({ ...f, randomize_questions: v }))}
          testId="admin-assessment-settings-randomize-q"
        />
        <ToggleField
          label="Randomize option order"
          checked={form.randomize_options}
          onChange={(v) => setForm((f) => ({ ...f, randomize_options: v }))}
          testId="admin-assessment-settings-randomize-o"
        />
      </div>
      <div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-assessment-settings-save"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--v2-accent)] text-white font-semibold hover:opacity-90 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

// -----------------------------
// Questions tab
// -----------------------------

function QuestionsTab({ questions, onAdd, onPatch, onDelete }) {
  return (
    <div data-testid="admin-assessment-questions" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-v2-text-muted">Question bank ({questions.length})</p>
        <button
          type="button"
          onClick={onAdd}
          data-testid="admin-assessment-question-add"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--v2-accent)] text-white text-sm font-semibold hover:opacity-90"
        >
          <Plus className="w-4 h-4" />
          Add question
        </button>
      </div>
      {questions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-v2-border p-8 text-center text-sm text-v2-text-muted">
          No questions yet.
        </div>
      ) : (
        questions.map((q) => (
          <QuestionRow key={q.id} question={q} onPatch={onPatch} onDelete={onDelete} />
        ))
      )}
    </div>
  );
}

function QuestionRow({ question, onPatch, onDelete }) {
  const [prompt, setPrompt] = useState(question.prompt_markdown || "");
  const [points, setPoints] = useState(question.points ?? 1);
  const [correct, setCorrect] = useState(question.correct_option_ids || []);
  const [explanation, setExplanation] = useState(question.explanation_markdown || "");

  const toggleCorrect = (oid) => {
    if (question.kind === "multi_choice") {
      const set = new Set(correct);
      if (set.has(oid)) set.delete(oid);
      else set.add(oid);
      setCorrect(Array.from(set));
    } else {
      setCorrect([oid]);
    }
  };

  const save = () => {
    onPatch(question.id, {
      prompt_markdown: prompt, points: Number(points),
      correct_option_ids: correct,
      explanation_markdown: explanation,
    });
  };

  return (
    <div
      data-testid={`admin-question-row-${question.id}`}
      className="rounded-xl border border-v2-border bg-v2-surface p-5 space-y-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">
          {question.kind} · {question.points ?? 1} pt{question.points === 1 ? "" : "s"}
        </div>
        <button
          type="button"
          onClick={() => onDelete(question.id)}
          data-testid={`admin-question-delete-${question.id}`}
          className="text-xs text-v2-text-muted hover:text-[var(--v2-danger)] inline-flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" />
          Delete
        </button>
      </div>
      <textarea
        data-testid={`admin-question-prompt-${question.id}`}
        value={prompt} onChange={(e) => setPrompt(e.target.value)}
        className="w-full rounded-lg border border-v2-border bg-v2-background p-3 text-v2-text h-20"
      />
      <div>
        <p className="text-xs text-v2-text-muted mb-2">Options — check the correct one(s)</p>
        <ul className="space-y-2">
          {(question.options || []).map((o) => {
            const checked = correct.includes(o.id);
            return (
              <li key={o.id} className="flex items-center gap-3">
                <input
                  type={question.kind === "multi_choice" ? "checkbox" : "radio"}
                  name={`correct-${question.id}`}
                  checked={checked}
                  onChange={() => toggleCorrect(o.id)}
                  data-testid={`admin-question-correct-${question.id}-${o.id}`}
                />
                <span className="text-sm text-v2-text">
                  <code className="text-xs text-v2-text-muted">{o.id}</code>{" · "}{o.text}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-v2-text-muted">
          Points
          <input type="number" min="0" value={points}
            onChange={(e) => setPoints(e.target.value)}
            data-testid={`admin-question-points-${question.id}`}
            className="mt-1 block w-full rounded-lg border border-v2-border bg-v2-background px-3 py-2 text-v2-text" />
        </label>
      </div>
      <textarea
        placeholder="Explanation (shown to learners when feedback policy is 'full')"
        value={explanation} onChange={(e) => setExplanation(e.target.value)}
        data-testid={`admin-question-explanation-${question.id}`}
        className="w-full rounded-lg border border-v2-border bg-v2-background p-3 text-v2-text h-20"
      />
      <div>
        <button
          type="button"
          onClick={save}
          data-testid={`admin-question-save-${question.id}`}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-v2-surface-muted border border-v2-border text-sm text-v2-text hover:border-v2-accent"
        >
          <CheckCircle2 className="w-3 h-3" />
          Save
        </button>
      </div>
    </div>
  );
}

// -----------------------------
// Preview tab (uses learner-sanitized preview endpoint)
// -----------------------------

function PreviewTab({ assessmentId }) {
  const [state, setState] = useState({ status: "loading", data: null });
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/admin/academy/assessments/${assessmentId}/preview`);
        setState({ status: "ready", data: res.data });
      } catch {
        setState({ status: "error", data: null });
      }
    })();
  }, [assessmentId]);

  if (state.status === "loading") return <Loader2 className="w-4 h-4 animate-spin text-v2-text-muted" />;
  if (state.status !== "ready" || !state.data) return <p className="text-sm text-v2-text-muted">Preview unavailable.</p>;

  const a = state.data.assessment || {};
  return (
    <div data-testid="admin-assessment-preview" className="rounded-xl border border-v2-border bg-v2-surface p-6 space-y-4">
      <p className="text-xs text-v2-text-muted">This is exactly what learners will see. Answer keys are stripped.</p>
      <h2 className="text-xl font-semibold text-v2-text" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
        {a.title}
      </h2>
      {a.description ? <p className="text-sm text-v2-text-muted whitespace-pre-wrap">{a.description}</p> : null}
      <ol className="space-y-3">
        {(state.data.questions || []).map((q, i) => (
          <li key={q.id} className="rounded-lg border border-v2-border bg-v2-background p-3">
            <p className="text-xs text-v2-text-muted">Question {i + 1}</p>
            <p className="text-sm text-v2-text mt-1">{q.prompt_markdown}</p>
            <ul className="mt-2 space-y-1 text-sm text-v2-text-muted">
              {(q.options || []).map((o) => (
                <li key={o.id}><code className="text-xs">{o.id}</code> · {o.text}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}

// -----------------------------
// Helpers
// -----------------------------

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-v2-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ToggleField({ label, checked, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-v2-text cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      {label}
    </label>
  );
}
